import { t as tr } from "@/i18n";
import { useState } from "react";
import { toast } from "sonner";
import { Bug, CloudSun, Coins, Eye, Globe, HeartHandshake, Landmark, Loader2, Shapes, Zap } from "lucide-react";
import { api, downloadFile, formatApiError, slug } from "@/lib/api";
import { generateSubjects } from "@/lib/generate";
import { useAuth } from "@/context/AuthContext";
import { Quiz } from "@/components/Quiz";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ICONS = { bug: Bug, globe: Globe, landmark: Landmark, "cloud-sun": CloudSun, "heart-handshake": HeartHandshake, shapes: Shapes };
const TINTS = ["bg-[#4ECDC4]", "bg-[#FFD166]", "bg-[#FF9FF3]", "bg-[#54A0FF]", "bg-[#1DD1A1]", "bg-[#FF9F43]"];

const TemplateCard = ({ t, i, busy, progress, onPick, onPreview }) => {
  const Icon = ICONS[t.icon] || Shapes;
  return (
    <div data-testid={`template-card-${t.id}`} className="card-soft p-5 text-left flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <span className={`w-12 h-12 rounded-2xl ${TINTS[i % TINTS.length]} border-2 border-[#1E293B] flex items-center justify-center shrink-0`}><Icon className="w-6 h-6" /></span>
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-100 rounded-full px-2.5 py-1">{t.subject_area} · {t.age_group}</span>
      </div>
      <h3 className="font-display text-lg font-semibold leading-tight">{t.title}</h3>
      <p className="text-sm text-slate-600 flex-1">{t.description}</p>
      {progress ? (
        <span className="text-sm font-bold text-[#FF5252] flex items-center gap-2" data-testid={`template-progress-${t.id}`}>
          <Loader2 className="w-4 h-4 animate-spin" /> {progress}
        </span>
      ) : (
        <span className="text-sm font-bold flex items-center gap-1.5"><Zap className="w-4 h-4 text-[#FF9F43]" /> {t.subjects.length} pages · <Coins className="w-3.5 h-3.5" /> {t.subjects.length} credits</span>
      )}
      <div className="flex gap-2 pt-1">
        <button onClick={() => onPreview(t)} disabled={busy} data-testid={`template-preview-${t.id}`}
          className="flex-1 btn-pop btn-white !py-2.5 !px-3 text-sm disabled:opacity-60 disabled:cursor-not-allowed"><Eye className="w-4 h-4" /> {tr("free_preview")}</button>
        <button onClick={() => onPick(t)} disabled={busy} data-testid={`template-create-${t.id}`}
          className="flex-1 btn-pop btn-red !py-2.5 !px-3 text-sm disabled:opacity-60 disabled:cursor-not-allowed">{tr("create")}</button>
      </div>
    </div>
  );
};

const TemplatePreview = ({ t, onClose, onCreate }) => (
  <Dialog open={!!t} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="rounded-3xl max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-preview-dialog">
      {t && (
        <>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{t.title} — sample page</DialogTitle>
            <DialogDescription>{tr("here_s_what_one_page_from_this_packet_lo")}</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="rounded-2xl border-4 border-[#1E293B] bg-white overflow-hidden shadow-[4px_6px_0_0_#1E293B]">
              <img src={`/templates/${t.id}.png`} alt={t.subjects[0]} className="w-full aspect-square object-contain" data-testid="template-preview-image"
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
              <div className="hidden w-full aspect-square items-center justify-center text-center text-sm font-bold text-slate-400 px-6" data-testid="template-preview-image-fallback">{tr("sample_picture_coming_soon_the_real_pack")}</div>
              <p className="text-xs font-bold text-center px-3 py-2 border-t-2 border-slate-100">{t.subjects[0]}</p>
            </div>
            <div className="space-y-4 text-sm">
              <div>
                <p className="font-bold text-slate-500 uppercase tracking-wider text-xs mb-1.5">{tr("pages_in_this_packet")}</p>
                <ol className="list-decimal pl-5 space-y-0.5 text-slate-700" data-testid="template-preview-pages">{t.subjects.map((s) => <li key={s}>{s}</li>)}</ol>
              </div>
              <div className="rounded-2xl bg-[#FFF9F2] border-2 border-[#FFD166] p-3">
                <p className="font-bold text-slate-500 uppercase tracking-wider text-xs mb-1">{tr("sample_fun_fact")}</p>
                <p data-testid="template-preview-fact">{t.preview.fact}</p>
              </div>
              <div className="rounded-2xl bg-[#F0FDFA] border-2 border-[#4ECDC4] p-3" data-testid="template-preview-quiz">
                <p className="font-bold text-slate-500 uppercase tracking-wider text-xs mb-1">{tr("sample_quiz_question_tap_an_answer")}</p>
                <Quiz questions={[t.preview.quiz]} testid="template-quiz" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="btn-pop btn-white !py-2.5 !px-4 text-sm" data-testid="template-preview-close">{tr("close")}</button>
            <button onClick={() => onCreate(t)} className="btn-pop btn-red !py-2.5 !px-4 text-sm" data-testid="template-preview-create">
              <Coins className="w-4 h-4" /> Create packet · {t.subjects.length} credits
            </button>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export const TemplateGallery = ({ templates, onDone, cover = {} }) => {
  const { user, setCredits } = useAuth();
  const [pending, setPending] = useState(null);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(null);
  const [progress, setProgress] = useState("");

  const run = async (t) => {
    setPending(null);
    setRunning(t.id);
    try {
      const pages = await generateSubjects(t.subjects, t.age_group, {
        category: "template",
        onCredits: setCredits,
        onProgress: (p) => setProgress(`Drawing ${p.done + 1} of ${p.total}...`),
        onError: (s, msg) => toast.error(`${s}: ${msg}`),
      });
      if (!pages.length) return;
      setProgress("Building PDF packet...");
      const { data } = await api.post("/packets", { title: t.title, page_ids: pages.map((p) => p.id), include_facts: true, include_quiz: true, ...cover }, { timeout: 120000 });
      await downloadFile(`/packets/${data.id}/pdf`, `${slug(data.title)}.pdf`);
      onDone(pages, data);
      if (pages.length < t.subjects.length) toast.warning(`"${t.title}" packet ready with ${pages.length} of ${t.subjects.length} pages — the rest couldn't be created.`);
      else toast.success(`"${t.title}" packet is ready!`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(null);
      setProgress("");
    }
  };

  return (
    <section className="space-y-5" data-testid="template-gallery">
      <div>
        <h2 className="font-display text-2xl font-semibold">{tr("ready_made_curriculum_packets")}</h2>
        <p className="text-slate-600">{tr("preview_any_packet_for_free_then_one_tap")}</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {templates.map((t, i) => (
          <TemplateCard key={t.id} t={t} i={i} busy={!!running} progress={running === t.id ? progress : ""} onPick={setPending} onPreview={setPreview} />
        ))}
      </div>
      <TemplatePreview t={preview} onClose={() => setPreview(null)} onCreate={(t) => { setPreview(null); setPending(t); }} />
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="template-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Create "{pending?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This uses {pending?.subjects.length} credits (you have {user.credits}). Pages: {pending?.subjects.join(", ")}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl" data-testid="template-cancel-button">{tr("cancel")}</AlertDialogCancel>
            <AlertDialogAction className="rounded-2xl bg-[#FF5252] hover:bg-[#E04343]" disabled={user.credits < (pending?.subjects.length || 0)}
              onClick={() => run(pending)} data-testid="template-confirm-button">
              {user.credits < (pending?.subjects.length || 0) ? "Not enough credits" : "Create packet"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
